/**
 * R11 G72 (PRQ-08.1/08.3) — the platform's Data pages share one frame: the sections the platform
 * declaration lists (GET /api/apps platform.dataSections: Model, Library, Sources, Quality) as tabs.
 * Nothing here names a section.
 */
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { type MenuItemDecl, useApps } from '@/features/apps/apps-api';
import { PageBody, PageHeader } from '@/features/governance/ui/primitives';

export function dataSectionsOf(platform: unknown): MenuItemDecl[] {
  const s = (platform as { dataSections?: MenuItemDecl[] } | undefined)?.dataSections;
  return Array.isArray(s) ? s : [];
}

/** Section tabs inside Data, in the L1 style: the current item is ink on surface-2, the rest muted; no underline, no outline. */
export function DataTabs({ label, tabs, activeId, className }: { label: string; tabs: readonly { id: string; label: string; to: string }[]; activeId: string; className?: string }) {
  return (
    <nav aria-label={label} className={cn('flex flex-wrap items-center gap-1 pb-4', className)}>
      {tabs.map((t) => {
        const active = t.id === activeId;
        return (
          <Link
            key={t.id}
            to={t.to}
            aria-current={active ? 'page' : undefined}
            className={cn('rounded-md px-3 py-1.5 text-sm font-medium no-underline hover:no-underline', active ? 'bg-surface-2 text-foreground' : 'text-muted-foreground hover:text-foreground')}
          >
            {t.label}
          </Link>
        );
      })}
    </nav>
  );
}

export function DataFrame({ sections, pathname, title, description, children }: { sections: readonly MenuItemDecl[]; pathname: string; title: string; description?: ReactNode; children: ReactNode }) {
  const active = sections.find((s) => pathname === (s.route.split(/[?#]/)[0] ?? ''))?.id ?? '';
  return (
    <PageBody>
      <PageHeader eyebrow="Data" title={title} description={description} />
      {sections.length ? <DataTabs label="Data" tabs={sections.map((s) => ({ id: s.id, label: s.label, to: s.route }))} activeId={active} /> : null}
      {children}
    </PageBody>
  );
}

export function DataPage(props: { title: string; description?: ReactNode; children: ReactNode }) {
  const apps = useApps();
  const { pathname } = useLocation();
  return <DataFrame sections={dataSectionsOf(apps.data?.platform)} pathname={pathname} {...props} />;
}
